import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  addByAge,
  compoundPrism,
  decentrationPrism,
  lensThickness,
  minimumBlankDiameter,
  nearPowerForDistance,
  obliqueMeridianPower,
  sphericalEquivalent,
  transpose,
  vertexCompensation,
  type PrismBase,
} from '../formulas';
import { formatFixed } from '../format';
import {
  NumField,
  Note,
  Pill,
  PresetButton,
  ResultBox,
  Row,
  SelectField,
  useNumberField,
} from '../ui/primitives';

// Блок 1 «Базовые расчёты» — калькуляторы 1–9.
// Начальные значения полей, шаги и подписи — из прототипа партнёра; вся
// арифметика вынесена в `../formulas` (чистые функции с юнит-тестами).

export function TranspositionCalc(): JSX.Element {
  const { t } = useTranslation();
  const sphere = useNumberField('-1.5');
  const cyl = useNumberField('-2');
  const axis = useNumberField('90');

  const result = transpose({ sphere: sphere.num, cyl: cyl.num, axis: axis.num });

  return (
    <div>
      <NumField label={t('calculators.transposition.sphere')} field={sphere} step="0.25" />
      <NumField label={t('calculators.transposition.cyl')} field={cyl} step="0.25" />
      <NumField label={t('calculators.transposition.axis')} field={axis} step="1" />
      <ResultBox>
        {t('calculators.transposition.result', {
          sphere: formatFixed(result.sphere),
          cyl: formatFixed(result.cyl),
          axis: result.axis,
        })}
      </ResultBox>
      <Note>{t('calculators.transposition.note')}</Note>
    </div>
  );
}

export function MinDiameterCalc(): JSX.Element {
  const { t } = useTranslation();
  const ed = useNumberField('52');
  const fpd = useNumberField('64');
  const pd = useNumberField('62');
  const margin = useNumberField('2');

  const { minDiameter } = minimumBlankDiameter({
    ed: ed.num,
    fpd: fpd.num,
    pd: pd.num,
    margin: margin.num,
  });

  return (
    <div>
      <NumField label={t('calculators.minDiameter.ed')} field={ed} step="1" />
      <NumField label={t('calculators.minDiameter.fpd')} field={fpd} step="1" />
      <NumField label={t('calculators.minDiameter.pd')} field={pd} step="1" />
      <NumField label={t('calculators.minDiameter.margin')} field={margin} step="0.5" />
      <ResultBox>
        {t('calculators.minDiameter.result', { value: formatFixed(minDiameter, 1) })}
      </ResultBox>
      <Note>{t('calculators.minDiameter.note')}</Note>
    </div>
  );
}

export function LensThicknessCalc(): JSX.Element {
  const { t } = useTranslation();
  const diameter = useNumberField('60');
  const power = useNumberField('-4');
  const index = useNumberField('1.6');
  const ref = useNumberField('1.2');

  const result = lensThickness({
    diameter: diameter.num,
    power: power.num,
    index: index.num,
    refThickness: ref.num,
  });

  return (
    <div>
      <NumField label={t('calculators.thickness.diameter')} field={diameter} step="1" />
      <NumField label={t('calculators.thickness.power')} field={power} step="0.25" />
      <NumField label={t('calculators.thickness.index')} field={index} step="0.01" />
      <NumField
        label={t(
          result.isMinus ? 'calculators.thickness.refCenter' : 'calculators.thickness.refEdge',
        )}
        field={ref}
        step="0.1"
      />
      <ResultBox>
        {t(
          result.isMinus
            ? 'calculators.thickness.resultEdge'
            : 'calculators.thickness.resultCenter',
          { value: formatFixed(result.result) },
        )}
      </ResultBox>
      <Note>{t('calculators.thickness.note')}</Note>
    </div>
  );
}

export function DecentrationPrismCalc(): JSX.Element {
  const { t } = useTranslation();
  const power = useNumberField('-3');
  const a = useNumberField('52');
  const dbl = useNumberField('18');
  const monoPd = useNumberField('31');

  const result = decentrationPrism({
    power: power.num,
    a: a.num,
    dbl: dbl.num,
    monoPd: monoPd.num,
  });

  return (
    <div>
      <NumField label={t('calculators.decentration.power')} field={power} step="0.25" />
      <NumField label={t('calculators.decentration.a')} field={a} step="1" />
      <NumField label={t('calculators.decentration.dbl')} field={dbl} step="1" />
      <NumField label={t('calculators.decentration.monoPd')} field={monoPd} step="0.5" />
      <ResultBox>
        <div>
          {t('calculators.decentration.decentration', {
            value: formatFixed(result.decentration, 1),
            direction: t(`calculators.decentration.${result.direction}`),
          })}
        </div>
        <div>{t('calculators.decentration.prism', { value: formatFixed(result.prism) })}</div>
      </ResultBox>
      <Note>{t('calculators.decentration.note')}</Note>
    </div>
  );
}

export function VertexCalc(): JSX.Element {
  const { t } = useTranslation();
  const power = useNumberField('-6');
  const oldVertex = useNumberField('12');
  const newVertex = useNumberField('0');

  const result = vertexCompensation({
    power: power.num,
    oldVertex: oldVertex.num,
    newVertex: newVertex.num,
  });

  return (
    <div>
      <Row>
        <PresetButton
          onClick={() => {
            oldVertex.set('12');
            newVertex.set('0');
          }}
        >
          {t('calculators.vertex.presetContacts')}
        </PresetButton>
        <PresetButton
          onClick={() => {
            oldVertex.set('8');
            newVertex.set('14');
          }}
        >
          {t('calculators.vertex.presetFrame')}
        </PresetButton>
      </Row>
      <NumField label={t('calculators.vertex.power')} field={power} step="0.25" />
      <NumField label={t('calculators.vertex.oldVertex')} field={oldVertex} step="1" />
      <NumField label={t('calculators.vertex.newVertex')} field={newVertex} step="1" />
      <ResultBox>
        {result === null
          ? t('calculators.vertex.empty')
          : t('calculators.vertex.result', { value: formatFixed(result) })}
      </ResultBox>
      <Note>{t('calculators.vertex.note')}</Note>
    </div>
  );
}

export function SphericalEquivalentCalc(): JSX.Element {
  const { t } = useTranslation();
  const sphere = useNumberField('-2');
  const cyl = useNumberField('-1');

  const se = sphericalEquivalent(sphere.num, cyl.num);

  return (
    <div>
      <NumField label={t('calculators.sphericalEquivalent.sphere')} field={sphere} step="0.25" />
      <NumField label={t('calculators.sphericalEquivalent.cyl')} field={cyl} step="0.25" />
      <ResultBox>
        {t('calculators.sphericalEquivalent.result', { value: formatFixed(se) })}
      </ResultBox>
      <Note>{t('calculators.sphericalEquivalent.note')}</Note>
    </div>
  );
}

export function ObliqueMeridianCalc(): JSX.Element {
  const { t } = useTranslation();
  const sphere = useNumberField('-1');
  const cyl = useNumberField('-2');
  const axis = useNumberField('180');
  const meridian = useNumberField('90');

  const power = obliqueMeridianPower({
    sphere: sphere.num,
    cyl: cyl.num,
    axis: axis.num,
    meridian: meridian.num,
  });

  return (
    <div>
      <NumField label={t('calculators.oblique.sphere')} field={sphere} step="0.25" />
      <NumField label={t('calculators.oblique.cyl')} field={cyl} step="0.25" />
      <NumField label={t('calculators.oblique.axis')} field={axis} step="1" />
      <NumField label={t('calculators.oblique.meridian')} field={meridian} step="1" />
      <ResultBox>
        {t('calculators.oblique.result', {
          meridian: meridian.value,
          value: formatFixed(power),
        })}
      </ResultBox>
      <Note>{t('calculators.oblique.note')}</Note>
    </div>
  );
}

export function NearIntermediateCalc(): JSX.Element {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'distance' | 'age'>('distance');
  const distancePower = useNumberField('-1');
  const add = useNumberField('2');
  const [distance, setDistance] = useState('0.4');
  const age = useNumberField('50');

  const needed = nearPowerForDistance({
    distancePower: distancePower.num,
    add: add.num,
    workingDistance: Number(distance),
  });

  return (
    <div>
      <Row>
        <Pill active={mode === 'distance'} onClick={() => setMode('distance')}>
          {t('calculators.near.modeDistance')}
        </Pill>
        <Pill active={mode === 'age'} onClick={() => setMode('age')}>
          {t('calculators.near.modeAge')}
        </Pill>
      </Row>
      {mode === 'distance' ? (
        <div>
          <NumField label={t('calculators.near.distancePower')} field={distancePower} step="0.25" />
          <NumField label={t('calculators.near.add')} field={add} step="0.25" />
          <SelectField
            label={t('calculators.near.workingDistance')}
            value={distance}
            onChange={setDistance}
            options={[
              { value: '0.4', label: t('calculators.near.option40') },
              { value: '0.65', label: t('calculators.near.option65') },
              { value: '1', label: t('calculators.near.option100') },
            ]}
          />
          <ResultBox>
            {t('calculators.near.result', { value: formatFixed(needed ?? Number.NaN) })}
          </ResultBox>
          <Note>{t('calculators.near.note')}</Note>
        </div>
      ) : (
        <div>
          <NumField label={t('calculators.near.age')} field={age} step="1" />
          <ResultBox>
            {t('calculators.near.ageResult', { value: formatFixed(addByAge(age.num)) })}
          </ResultBox>
          <Note>{t('calculators.near.ageNote')}</Note>
        </div>
      )}
    </div>
  );
}

const PRISM_BASES: PrismBase[] = ['up', 'down', 'in', 'out'];

export function CompoundPrismCalc(): JSX.Element {
  const { t } = useTranslation();
  const first = useNumberField('3');
  const second = useNumberField('4');
  const [firstBase, setFirstBase] = useState<PrismBase>('out');
  const [secondBase, setSecondBase] = useState<PrismBase>('up');

  const result = compoundPrism(
    { magnitude: first.num, base: firstBase },
    { magnitude: second.num, base: secondBase },
  );
  const baseLabel = [result.vertical, result.horizontal]
    .filter((part): part is PrismBase => part !== null)
    .map((part) => t(`calculators.compoundPrism.${part}`))
    .join('-');

  const options = PRISM_BASES.map((base) => ({
    value: base,
    label: t(`calculators.compoundPrism.option${base.charAt(0).toUpperCase()}${base.slice(1)}`),
  }));

  return (
    <div>
      <NumField label={t('calculators.compoundPrism.first')} field={first} step="0.25" />
      <SelectField
        label={t('calculators.compoundPrism.firstBase')}
        value={firstBase}
        onChange={(value) => setFirstBase(value as PrismBase)}
        options={options}
      />
      <NumField label={t('calculators.compoundPrism.second')} field={second} step="0.25" />
      <SelectField
        label={t('calculators.compoundPrism.secondBase')}
        value={secondBase}
        onChange={(value) => setSecondBase(value as PrismBase)}
        options={options}
      />
      <ResultBox>
        {baseLabel === ''
          ? t('calculators.compoundPrism.resultZero')
          : t('calculators.compoundPrism.result', {
              value: formatFixed(result.magnitude),
              base: baseLabel,
              angle: formatFixed(result.angleDeg, 0),
            })}
      </ResultBox>
      <Note>{t('calculators.compoundPrism.note')}</Note>
    </div>
  );
}
